import * as eu from './regions/eu';
import * as ie from './regions/ie';
import * as my from './regions/my';
import * as sg from './regions/sg';
import * as uk from './regions/uk';
import * as uksef from './regions/uksef';

// One module per region in ./regions holds everything that differs for it. To add a region: create the module
// (copy the closest existing one) and list it here. UK is first because it is the default for unknown codes.
const REGION_MODULES = [uk, sg, my, uksef, ie, eu];

export const REGION_CONFIGS = Object.fromEntries(REGION_MODULES.map((region) => [region.config.code, region.config]));

const REGION_HOSTS = Object.fromEntries(
  REGION_MODULES.flatMap((region) => region.hosts.map((host) => [host, region.config.code])),
);

const REGION_PRIMARY_HOSTS = Object.fromEntries(REGION_MODULES.map((region) => [region.config.code, region.primaryHost]));

const SOURCE_SITE_REGIONS = Object.fromEntries(
  REGION_MODULES.map((region) => [region.config.sourceSite, region.config.code]),
);

function getRegionCodeFromReferrer(referrer) {
  try {
    const host = new URL(referrer).hostname.toLowerCase();
    if (host === 'uksef.digitalreporting.uk') return 'UKSEF';
    return getRegionCodeFromHostname(host);
  } catch {
    return null;
  }
}

export function normalizeRegionCode(value) {
  const code = String(value || '').trim().toUpperCase();
  return REGION_CONFIGS[code] ? code : 'UK';
}

export function getRegionConfig(regionCode) {
  return REGION_CONFIGS[normalizeRegionCode(regionCode)];
}

export function getRegionCodeFromHostname(hostname) {
  const host = String(hostname || '').split(':')[0].toLowerCase();
  return REGION_HOSTS[host] || null;
}

export function getRegionCodeFromMetadata(metadata = {}) {
  const region = normalizeRegionCode(metadata.region_code);
  const sourceSite = String(metadata.source_site || '').trim().toLowerCase();

  if (metadata.region_code && REGION_CONFIGS[region]) return region;
  if (SOURCE_SITE_REGIONS[sourceSite]) return SOURCE_SITE_REGIONS[sourceSite];
  return null;
}

export function getPrimaryHostForRegion(regionCode) {
  return REGION_PRIMARY_HOSTS[normalizeRegionCode(regionCode)] || REGION_PRIMARY_HOSTS.UK;
}

export function storePortalContext(params) {
  if (!params || typeof window === 'undefined') return;
  const hostRegion = getRegionCodeFromHostname(window.location.hostname);
  const region = normalizeRegionCode(params.get('region') || hostRegion || sessionStorage.getItem('portal_region') || process.env.REACT_APP_REGION);
  const sourceSite = params.get('source_site') || REGION_CONFIGS[region].sourceSite;
  sessionStorage.setItem('portal_region', region);
  sessionStorage.setItem('portal_source_site', sourceSite);
}

export function getActiveRegionConfig() {
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    const hostRegion = getRegionCodeFromHostname(window.location.hostname);
    const referrerRegion = getRegionCodeFromReferrer(document.referrer);
    if (params.has('region') || params.has('source_site') || hostRegion) {
      storePortalContext(params);
    }
    const stored = sessionStorage.getItem('portal_region');
    if (params.has('region')) return REGION_CONFIGS[normalizeRegionCode(params.get('region'))];
    if (params.has('source_site')) {
      const sourceRegion = getRegionCodeFromMetadata({ source_site: params.get('source_site') }) || normalizeRegionCode(stored);
      sessionStorage.setItem('portal_region', sourceRegion);
      sessionStorage.setItem('portal_source_site', REGION_CONFIGS[sourceRegion].sourceSite);
      return REGION_CONFIGS[sourceRegion];
    }
    if (referrerRegion && hostRegion === 'UK') {
      sessionStorage.setItem('portal_region', referrerRegion);
      sessionStorage.setItem('portal_source_site', REGION_CONFIGS[referrerRegion].sourceSite);
      return REGION_CONFIGS[referrerRegion];
    }
    if (hostRegion) return REGION_CONFIGS[hostRegion];
    if (referrerRegion) {
      sessionStorage.setItem('portal_region', referrerRegion);
      sessionStorage.setItem('portal_source_site', REGION_CONFIGS[referrerRegion].sourceSite);
      return REGION_CONFIGS[referrerRegion];
    }
    if (stored) return REGION_CONFIGS[normalizeRegionCode(stored)];
  }
  return REGION_CONFIGS[normalizeRegionCode(process.env.REACT_APP_REGION)];
}

export function getPortalHeaders(regionConfig = getActiveRegionConfig()) {
  return {
    'X-Portal-Region': regionConfig.code,
    'X-Portal-Source-Site': regionConfig.sourceSite,
  };
}

export function formatRegionMoney(amount, regionConfig = getActiveRegionConfig()) {
  return `${regionConfig.moneyPrefix}${Number(amount || 0).toLocaleString()}`;
}

export function formatRegionMoneyMinor(minorUnits, regionConfig = getActiveRegionConfig()) {
  if (minorUnits == null) return '-';
  return `${regionConfig.moneyPrefix}${(Number(minorUnits || 0) / 100).toFixed(2)}`;
}
